import { alpha, createTheme, type Theme } from '@mui/material/styles';

// Typescript
import {
  FontPropType,
  OwnerStateThemeType,
  PaletteOptionsProps,
} from './TypeScriptProps';

const getFontStyle: FontPropType = (tmpWeight, tmpSize) => ({
  fontWeight: tmpWeight,
  fontSize: tmpSize,
});

const colors: PaletteOptionsProps = {
  primary: {
    main: '#2563EB',
    light: '#DBEAFE',
    900: '#1E3A8A',
    800: '#1E40AF',
    700: '#1D4ED8',
    600: '#2563EB',
    500: '#3B82F6',
    400: '#60A5FA',
    300: '#93C5FD',
    200: '#BFDBFE',
    100: '#DBEAFE',
    50: '#EFF6FF',
  },
  secondary: {
    main: '#0EA5E9',
    500: '#0EA5E9',
    50: '#E0F2FE',
  },
  tertiary: {
    main: '#F97316',
    500: '#F97316',
    50: '#FFF7ED',
    contrastText: '#fff',
  },
  info: {
    main: '#0EA5E9',
  },
  error: {
    main: '#EF4444',
  },
  warning: {
    main: '#F59E0B',
  },
  success: {
    main: '#22C55E',
  },
  disabled: {
    main: '#E2E8F0',
  },
  neutral: {
    main: '#94A3B8',
    max: '#0F172A',
    900: '#0F172A',
    800: '#1E293B',
    700: '#334155',
    600: '#475569',
    500: '#64748B',
    400: '#94A3B8',
    300: '#CBD5E1',
    200: '#E2E8F0',
    100: '#F1F5F9',
    50: '#F8FAFC',
    min: '#ffffff',
  },
};

const neutral = colors.neutral || {
  main: '#94A3B8',
  max: '#0F172A',
  900: '#0F172A',
  800: '#1E293B',
  700: '#334155',
  600: '#475569',
  500: '#64748B',
  400: '#94A3B8',
  300: '#CBD5E1',
  200: '#E2E8F0',
  100: '#F1F5F9',
  50: '#F8FAFC',
  min: '#ffffff',
};

const primary = colors.primary || {
  main: '#2563EB',
  light: '#DBEAFE',
  900: '#1E3A8A',
  800: '#1E40AF',
  700: '#1D4ED8',
  600: '#2563EB',
  500: '#3B82F6',
  400: '#60A5FA',
  300: '#93C5FD',
  200: '#BFDBFE',
  100: '#DBEAFE',
  50: '#EFF6FF',
};
const primaryMain = (primary as { main?: string }).main || '#2563EB';
const primaryShade = (key: number, fallback?: string): string => (
  (primary as Record<string, string>)[String(key)] || fallback || primaryMain
);

const basicTheme = createTheme({
  typography: {
    fontFamily: [
      'IBM Plex Sans',
      'Inter',
      'Segoe UI',
      'Roboto',
      'Helvetica',
      'Arial',
      'sans-serif',
    ].join(','),
    fontSize: 16,
    buttonText1: { ...getFontStyle('500', '1rem') }, // 500 16px
    buttonText2: { ...getFontStyle('500', '0.875rem') }, // 500 14px
    buttonText3: { ...getFontStyle('500', '0.75rem') }, // 500 12px
    link1: { ...getFontStyle('400', '1rem') }, // 400 16px
    link2: { ...getFontStyle('400', '0.875rem') }, // 400 14px
    link3: { ...getFontStyle('400', '0.75rem') }, // 400 12px
  },
  palette: {
    ...colors,
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 360,
      md: 768,
      lg: 1200,
      xl: 1536,
    },
  },
  // material-ui v5 覆寫 components style 的方式
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontStyle: 'normal',
          color: neutral[900],
        },
        h1: {
          ...getFontStyle('500', '6rem'), // 500 96px
        },
        h2: {
          ...getFontStyle('500', '3.75rem'), // 500 60px
        },
        h3: {
          ...getFontStyle('500', '3rem'), // 500 48px
        },
        h4: {
          ...getFontStyle('500', '2.125rem'), // 500 34px
        },
        h5: {
          ...getFontStyle('500', '1.5rem'), // 500 24px
        },
        h6: {
          ...getFontStyle('500', '1.25rem'), // 500 20px
        },
        caption: {
          ...getFontStyle('400', '0.75rem'), // 400 12px
        },
        subtitle1: {
          ...getFontStyle('500', '1rem'), // 500 16px
        },
        subtitle2: {
          ...getFontStyle('500', '0.875rem'), // 500 14px
        },
        body1: {
          ...getFontStyle('400', '1rem'), // 400 16px
        },
        body2: {
          ...getFontStyle('400', '0.875rem'), // 400 14px
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F8FAFC',
          color: neutral[900],
        },
      },
    },
  },
});

// extend basic custom theme
const customTheme = createTheme(basicTheme, {
  components: {
    MuiGrid: {
      defaultProps: {
        columns: {
          xs: 4,
          sm: 8,
          md: 12,
          lg: 12,
        },
      },
      styleOverrides: {
        root: {
          width: '100%',
          margin: 0,
          justifyContent: 'space-between',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          border: `1px solid ${neutral[200]}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: `1px solid ${neutral[200]}`,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: neutral[900],
          color: neutral[50],
          border: 'none',
          borderRight: `1px solid ${neutral[800]}`,
          '& .MuiListItemIcon-root': {
            color: neutral[50],
          },
          '& .MuiListItemText-primary': {
            color: neutral[50],
          },
          '& .MuiDivider-root': {
            borderColor: neutral[800],
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '4px 8px',
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: alpha(primaryShade(600), 0.25),
            boxShadow: `inset 0 0 0 1px ${alpha(primaryShade(400), 0.5)}`,
          },
          '&:hover': {
            backgroundColor: alpha(primaryShade(600), 0.12),
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: neutral[200],
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[50],
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: neutral[50],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: neutral[300],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: primary[400],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryShade(500),
            boxShadow: `0 0 0 2px ${alpha(primaryShade(500), 0.2)}`,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          border: `1px solid ${neutral[200]}`,
          overflow: 'hidden',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[50],
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: neutral[50],
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${neutral[200]}`,
        },
        head: {
          fontWeight: 600,
          color: neutral[700],
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
    MuiStack: {
      defaultProps: {
        spacing: 2,
      },
    },
    MuiChip: {
      defaultProps: {},
      styleOverrides: {
        root: {
          color: 'white',
        },
      },
    },
    MuiButton: {
      variants: [{ props: { variant: 'secondary' } }],
      styleOverrides: {
        root: ({ ownerState, theme }: OwnerStateThemeType) => {
          const variant = ownerState.variant || 'text';
          const isFocus = ownerState['data-focus'];

          return {
            '&.MuiButton-root': {
              textTransform: 'none',

              // contained
              ...(variant === 'contained' && {
                color: theme.palette?.neutral.min,
                backgroundColor: theme.palette?.primary?.main,
                ...(isFocus
                  ? {
                    backgroundColor: theme.palette?.primary?.[700],
                    boxShadow: `0 0 16px 0 ${alpha(
                      theme.palette?.primary?.[500],
                      0.3,
                    )}`,
                  }
                  : {}),
                '&:hover': { backgroundColor: theme.palette?.primary?.[700] },
                // "&:focus": {
                //   backgroundColor: theme.palette?.primary?.[700],
                //   boxShadow: `0 0 16px 0 ${alpha(
                //     theme.palette?.primary?.[500],
                //     0.3,
                //   )}`,
                // },
                '&:disabled': {
                  color: theme.palette?.neutral?.[400],
                  backgroundColor: theme.palette?.disabled?.main,
                },
              }),
              // secondary
              ...(variant === 'secondary' && {
                color: basicTheme.palette.primary[500],
                backgroundColor: theme.palette.primary[50],
                ...(isFocus
                  ? {
                    backgroundColor: theme.palette?.primary?.[100],
                    boxShadow: `0 0 16px 0 ${alpha(
                      theme.palette?.primary?.[500],
                      0.3,
                    )}`,
                  }
                  : {}),
                '&:hover': {
                  backgroundColor: theme.palette.primary[100],
                },
                // "&:focus": {
                //   backgroundColor: theme.palette?.primary?.[100],
                //   boxShadow: `0 0 16px 0 ${alpha(
                //     theme.palette?.primary?.[500],
                //     0.3,
                //   )}`,
                // },
                '&:disabled': {
                  color: theme.palette.neutral[400],
                  backgroundColor: theme.palette.neutral[200],
                },
              }),
              // outlined
              ...(variant === 'outlined' && {
                color: theme.palette?.primary?.main,
                border: `${theme.palette?.primary?.[500]} 1px solid`,
                ...(isFocus
                  ? {
                    backgroundColor: theme.palette?.primary?.[100],
                    boxShadow: `0 0 16px 0 ${alpha(
                      theme.palette?.primary?.[500],
                      0.3,
                    )}`,
                  }
                  : {}),
                '&:hover': {
                  color: theme.palette?.primary?.[500],
                  backgroundColor: theme.palette?.primary?.[100],
                },
                // "&:focus": {
                //   backgroundColor: theme.palette?.primary?.[100],
                //   boxShadow: `0 0 16px 0 ${alpha(
                //     theme.palette?.primary?.[500],
                //     0.3,
                //   )}`,
                // },
                '&:disabled': {
                  color: theme.palette?.neutral?.[400],
                  border: `${theme.palette?.neutral?.[200]} 1px solid `,
                },
              }),
              // text
              ...(variant === 'text' && {
                ...(isFocus
                  ? {
                    backgroundColor: theme.palette?.primary?.[100],
                    boxShadow: `0 0 16px 0 ${alpha(
                      theme.palette?.primary?.[500],
                      0.3,
                    )}`,
                  }
                  : {}),
                '&:hover': {
                  color: theme.palette?.primary?.[500],
                  backgroundColor: theme.palette?.primary?.[100],
                },
                // "&:focus": {
                //   backgroundColor: theme.palette?.primary?.[100],
                //   boxShadow: `0 0 16px 0 ${alpha(
                //     theme.palette?.primary?.[500],
                //     0.3,
                //   )}`,
                // },
                '&:disabled': {
                  color: theme.palette?.neutral?.[400],
                },
              }),
            },
          };
        },
        sizeLarge: ({ theme }: { theme: Theme }) => ({
          borderRadius: '8px',
          lineHeight: '18.75px',
          padding: '1rem 1.25rem',
          ...theme.typography?.buttonText1,
        }),
        sizeMedium: ({ theme }: { theme: Theme }) => ({
          borderRadius: '6px',
          lineHeight: '16.41px',
          padding: '0.75rem 1rem',
          ...theme.typography?.buttonText2,
        }),
        sizeSmall: ({ theme }: { theme: Theme }) => ({
          borderRadius: '6px',
          lineHeight: '14.06px',
          padding: '0.5rem 0.75rem',
          ...theme.typography?.buttonText3,
        }),
      },
    },
  },
});

export default customTheme;
